import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Carousel from './carousel';
import Image from './image';
import Lightbox from './lightbox';
import Media from './media';
import MagnifyHint from 'react-storefront/carousel/MagnifyHint';
import CarouselThumbnails from './carouselThumbnails';
import get from 'lodash/get';
import { useTranslation } from "../../../../tools/i18n";
import { TrackingContext } from "../../../providers/tracking";
import { ImageGalleryIndexContext } from "../../../providers/imageGalleryIndex"
import Maximize from "public/images/icons/maximize.svg";

const THUMBNAIL_IMAGE_ID = '__rsf-placeholder-thumbnail';

const useStyles = makeStyles(theme => ({
	/**
	 * Styles applied to the root component.
	 */
	root: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		[theme.breakpoints.up('sm')]: {
			overflow: 'hidden',
		},
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
			'&.lightboxActive': {
				flexDirection: 'column',
				overflow: 'hidden'
			}
		},
	},
	/**
	 * Styles applied to the root component when `thumbnailPosition` is `left` or `right`.
	 */
	rootSideThumbs: {
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
		},
	},
	/**
	 * Styles applied to the wrapper element of each media component.
	 */
	mediaWrap: {
		height: '100%',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'stretch',
		'& img': {
			maxHeight: '100%',
			maxWidth: '100%',
			objectFit: 'contain',
		},
	},
	/**
	 * Styles applied to each of the thumbnail elements.
	 */
	thumbnail: {
		position: 'absolute',
		top: 0,
		left: 0,
		height: '100%',
		width: '100%',
	},
	/**
	 * Styles applied to the thumbnail component when thumbnailPosition is `left`.
	 */
	thumbnailsLeft: {
		[theme.breakpoints.up('sm')]: {
			order: -1,
		},
	},
	/**
	 * Styles applied to the thumbnail component when thumbnailPosition is `top`.
	 */
	thumbnailsTop: {
		order: -1,
	},
	/**
	 * Styles applied to the carousel component when the lightbox is shown.
	 */
	lightboxCarousel: {
		flex: 1,
		justifyContent: 'center',
		overflow: 'hidden',
	},
	/**
	 * Styles applied to the thumbnails in the lightbox.
	 */
	lightboxThumbs: {
		paddingBottom: theme.spacing(2),
	},
	modelViewerWrapper: {
		position: 'relative',
		width: '100%',
		height: '100%',
		zIndex: 100,
		margin: 0,
		backgroundColor: theme.palette.primary.background,
		'& model-viewer': {
			display: 'block !important'
		},
		[theme.breakpoints.down('sm')]: {
			height: 'calc(125vw)',
			'& #threeDViewer': {
				opacity: 0.75,
				pointerEvents: 'none'
			},
		},
		'&.active #threeDViewer': {
			opacity: 1,
			pointerEvents: 'all'
		}
	},
	galleryScroll: {
		position: 'absolute',
		bottom: 10,
		left: '50%',
		padding: '6px 14px',
		fontSize: 12,
		letterSpacing: 1,
		color: theme.palette.primary.background,
		backgroundColor: '#000',
		transform: 'translate(-50%, 0)',
		'&:hover, &:active': {
			backgroundColor: '#000',
		},
		[theme.breakpoints.up('sm')]: {
			display: 'none'
		}
	},
    customMaximize: {
        display: 'none',
        position: 'absolute',
        top: 15,
        left: 110,
        background: 'transparent',
        width: 22,
        height: 22,
        fill: '#fff',
        mixBlendMode: 'difference',
        [theme.breakpoints.down('xs')]: {
            left: 15,
        }
    }
}), {name: 'RSFMediaCarousel'});

function MediaCarousel(props) {
	let {
		thumbnails,
		thumbnail,
		thumbsClassName,
		thumbnailPosition,
		magnifyHintClassName,
		videoProps,
		imageProps,
		lightboxProps,
		lightboxClassName,
		arrows,
		classes,
		media,
		productNumber,
		product,
		isOnSale,
		magnifyProps,
		id,
		CarouselComponent,
		ImageComponent,
		MediaComponent,
		CarouselThumbnailsComponent,
		...others
	} = props;

	const [imagesLoaded, setImagesLoaded] = useState(false);
	const styles = useStyles();
	const { t } = useTranslation(['general']);
	const ref = useRef(null);
	const [over, setOver] = useState(false);
	const [selected, setSelected] = useState(0);
	const [video, setVideo] = useState(false);
	const [lightboxActive, setLightboxActive] = useState(false);
	const {actions: trackingActions} = useContext(TrackingContext);
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
	const isThumbsSide = ['right', 'left']?.includes(thumbnailPosition);
	const { imageGalleryIndex, setImageGalleryIndex } = useContext(ImageGalleryIndexContext);
    let showHint = magnifyProps?.show || false;

	useEffect(() => {
		// Reset selection index when media changes
		setSelected(0);
	}, [media]);

	useEffect(() => {
		if (media && media.full && media.full[selected] && media.full[selected].type === 'video') {
			setVideo(true);
		} else {
			setVideo(false);
		}
		if (selected >= get(media, 'full', []).length) {
			setSelected(0)
		}

		setImageGalleryIndex(selected)
	}, [selected]);

	const onFullSizeImagesLoaded = useCallback(() => {
		setImagesLoaded(true);
	}, []);

	const onClickCarousel = useCallback(evt => {
		if (!evt.defaultPrevented) {
			setLightboxActive(!lightboxActive);
		}
	});

	useEffect(() => {
		if (!ref.current || imagesLoaded || !thumbnail) return;
		const firstImage = ref.current.querySelector('img');
		if (firstImage && firstImage.id !== THUMBNAIL_IMAGE_ID) {
			firstImage.addEventListener('load', onFullSizeImagesLoaded);
			return () => firstImage.removeEventListener('load', onFullSizeImagesLoaded);
		}
	}, [media, imagesLoaded, thumbnail]);

	const belowAdornments = [];

	if (thumbnail && !imagesLoaded) {
		belowAdornments.push(
			<ImageComponent
				id={THUMBNAIL_IMAGE_ID}
				key="thumbnail"
				className={styles.thumbnail}
				fill
				{...thumbnail}
			/>
		);
	}

	if (media && media.full && media.full.some(item => item.magnify)) {
		// we use the media's magnify.width prop to test if the image is larger than the screen size, and
		// hide the magnify hint if so. this is a magic large number just used to ensure that the hint is
		// shown if the width property is not defined for the given media
		const MAX_WIDTH = 10000;

		if (lightboxActive) {
				showHint = false;
			const {innerWidth} = window;
			const mediaWidth = get(media.full[selected], 'magnify.width', MAX_WIDTH);
			if (mediaWidth <= innerWidth) {
				showHint = false;
			}
		}

		if (showHint && !video) {
			belowAdornments.push(
                <Maximize width={20} className={styles.customMaximize}/>
			);
		}
	}

	if (lightboxActive) {
		Object.assign(others, {
			autoPlay: false,
			className: clsx(
				others.className,
				styles.lightboxCarousel,
				lightboxActive && lightboxClassName,
			),
			height: isSmall ? '100%' : null,
			slideStyle: {...(others.slideStyle || {}), display: 'flex', justifyContent: 'center'},
		});
	}

	function handleLightboxClose() {
        setLightboxActive(!lightboxActive);
    }


	function renderCarouselItems() {
		let items = [];
		let carouselItems = get(media, 'full', []);

		items = carouselItems.map((item, i) => {
			let magnify = lightboxActive ? item.magnify : false;
			const magnifySrc = get(item, 'magnify.src', item.src);

			if (lightboxActive) {
				const {innerWidth, innerHeight} = window;

				if (item.magnify && item.magnify.width <= innerWidth && item.magnify.height <= innerHeight) {
					magnify = false;
				}
			}

			return (
				<MediaComponent
					key={i}
					magnifyProps={magnifyProps}
					{...item}
					alt={item.alt}
					product={product}
					magnify={magnify}
					src={lightboxActive ? magnifySrc : item.src}
					lightboxActive={lightboxActive}
					imageProps={
						lightboxActive
							? {
								fill: false,
								contain: true,
								src: magnifySrc,
								alt: item.alt
							}
							: { ...imageProps, alt: item.alt }
					}
					videoProps={videoProps}
				/>
			);
		});

		return items;
	}

	const body = (
		<div
			className={[clsx(styles.root, {
				[styles.rootSideThumbs]: !lightboxActive && isThumbsSide,
			}), lightboxActive ? 'lightboxActive' : '', isOnSale ? 'isOnSale' : '' ].join(' ')}
		>

			<CarouselComponent
				id={id}
				ref={ref}
				belowAdornments={belowAdornments}
				classes={{...classes}}
				onClick={onClickCarousel}
				selected={selected}
				arrows={arrows ? 'all' : false}
				setSelected={setSelected}
				height={ (100 * props.aspectRatio) + '%'}
				style={{cursor: 'pointer'}}
				indicators={true}
				lightboxActive={lightboxActive}
				{...others}
			>

				{renderCarouselItems()}
			</CarouselComponent>
			{thumbnails && media && (
				<CarouselThumbnailsComponent
					className={clsx(
						thumbsClassName,
						lightboxActive && styles.lightboxThumbs,
						!lightboxActive && thumbnailPosition === 'left' && styles.thumbnailsLeft,
						!lightboxActive && thumbnailPosition === 'top' && styles.thumbnailsTop,
					)}
					bind={`${id}.index`}
					carouselId={id}
					selected={selected}
					setSelected={setSelected}
					lightboxActive={lightboxActive}
					thumbnails={media.thumbnails}
					thumbnailPosition={lightboxActive ? 'bottom' : thumbnailPosition}
				/>
			)}
		</div>
	);

	return (
		<>
			{lightboxActive ?
                <Lightbox {...lightboxProps} open={!!lightboxActive} onClose={handleLightboxClose}>
                    {body}
                </Lightbox>
                :
                body
            }
		</>
	);
}

MediaCarousel.propTypes = {
	/**
	 * Override or extend the styles applied to the component. See [CSS API](#css) below for more details.
	 */
	classes: PropTypes.object,

	/**
	 * If `true`, the carousel will have thumbnails below it.
	 */
	thumbnails: PropTypes.bool,
	/**
	 * Data for an image to be used for a thumbnail when the images are not yet loaded.
	 */
	thumbnail: PropTypes.shape({
		src: PropTypes.string,
		alt: PropTypes.string,
	}),
	/**
	 * An optional `className` to use for the thumbnails component.
	 */
	thumbsClassName: PropTypes.string,
	/**
	 * An optional `className` to use for the carousel component when the lightbox is open.
	 */
	lightboxClassName: PropTypes.string,
	/**
	 * Position of thumbnails, relative to the main carousel image. For small screens, the position
	 * will revert to `top` when this value is set to `left` or `right`.
	 *
	 * This property will have no effect in Amp.
	 */
	thumbnailPosition: PropTypes.oneOf(['bottom', 'top', 'left', 'right']),
	/**
	 * A component type to use for the thumbnails.
	 */
	CarouselThumbnailsComponent: PropTypes.elementType,

	/**
	 * An optional `className` to use for the [`MagnifyHint`](/apiReference/carousel/MagnifyHint) component.
	 */
	magnifyHintClassName: PropTypes.string,
	/**
	 * Props passed through to each [`Media`](/apiReference/carousel/Media)'s video component.
	 */
	videoProps: PropTypes.object,
	/**
	 * Props passed through to each [`Media`](/apiReference/carousel/Media)'s
	 * [`imageProps`](/apiReference/carousel/Media#prop-imageProps).
	 */
	imageProps: PropTypes.object,
	/**
	 * Data for all the media objects to show in the carousel.
	 */
	media: PropTypes.shape({
		full: PropTypes.arrayOf(PropTypes.object),
		thumbnails: PropTypes.arrayOf(PropTypes.object),
	}),
	/**
	 * Props passed through to each [`Media`](/apiReference/carousel/Media)'s
	 * [`magnifyProps`](/apiReference/carousel/Media#prop-magnifyProps).
	 */
	magnifyProps: PropTypes.object,
	/**
	 * A component type to use for each media object in the carousel.
	 */
	MediaComponent: PropTypes.elementType,

	/**
	 * Props passed through to the [`Lightbox`](/apiReference/carousel/Lightbox).
	 */
	lightboxProps: PropTypes.object,

	/**
	 * An `id` attribute to use for the carousel's root element.
	 */
	id: PropTypes.string,

	/**
	 * A component type to use for the main carousel component.
	 */
	CarouselComponent: PropTypes.elementType,

	/**
	 * The component type to use to display images.
	 */
	ImageComponent: PropTypes.elementType,
};

MediaCarousel.defaultProps = {
	lightboxProps: {},
	magnifyProps: {},
	thumbnails: true,
	thumbnailPosition: 'bottom',
	MediaComponent: Media,
	ImageComponent: Image,
	CarouselComponent: Carousel,
	CarouselThumbnailsComponent: CarouselThumbnails,
	videoProps: {
		controls: true,
		autoPlay: true,
		muted: true,
		playsInline: true
	},
};

export default MediaCarousel;
